import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Storage key used to persist the chosen language in cache (localStorage)
const LANGUAGE_STORAGE_KEY = 'app_language';

// Supported languages
export const LANGUAGES = {
  fr: { code: 'fr', label: 'Français', dir: 'ltr', flag: '🇫🇷' },
  ar: { code: 'ar', label: 'العربية', dir: 'ltr', flag: '🇸🇦' },
};

const DEFAULT_LANGUAGE = 'fr';

// Translation dictionary. Add keys here as more of the UI gets translated.
const TRANSLATIONS = {
  fr: {
    'nav.home': 'Accueil',
    'nav.administration': 'Administration',
    'nav.parametrage': 'Paramétrage',
    'nav.documentation': 'Documentation',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.profile': 'Profil',
    'nav.cart': 'Panier',
    'nav.language': 'Langue',

    // Admin sidebar navigation
    'admin.nav.dashboard': 'Tableau de bord',
    'admin.nav.products': 'Produits',
    'admin.nav.allProducts': 'Tous les produits',
    'admin.nav.variants': 'Variantes',
    'admin.nav.categories': 'Catégories',
    'admin.nav.brands': 'Marques',
    'admin.nav.suppliers': 'Fournisseurs',
    'admin.nav.orders': 'Commandes',
    'admin.nav.allOrders': 'Toutes les commandes',
    'admin.nav.deliveryCompany': 'Société de Livraison',
    'admin.nav.exchange': 'Échange',
    'admin.nav.charges': 'Charges',
    'admin.nav.barcodeVerification': 'Vérification Code-Barres',
    'admin.nav.returnReceived': 'Retour Reçu',
    'admin.nav.purchases': 'Achats',
    'admin.nav.purchasesList': 'Liste des achats',
    'admin.nav.supplierPayment': 'Règlement Fournisseur',
    'admin.nav.supplierConsumption': 'Consommation Fournisseur',
    'admin.nav.smsing': 'SMSing',
    'admin.nav.promoCodes': 'Codes Promo',
    'admin.nav.socialLeads': 'Leads Réseaux Sociaux',
    'admin.nav.settings': 'Paramètres',

    // Admin page titles (TopBar)
    'admin.title.dashboard': 'Tableau de bord',
    'admin.title.products': 'Gestion des produits',
    'admin.title.variants': 'Gestion des variantes',
    'admin.title.brands': 'Gestion des marques',
    'admin.title.suppliers': 'Gestion des fournisseurs',
    'admin.title.orders': 'Gestion des commandes',
    'admin.title.deliveries': 'Gestion des Livraisons',
    'admin.title.customers': 'Gestion des clients',
    'admin.title.settings': 'Paramètres',
    'admin.title.purchases': 'Gestion des achats',
    'admin.title.exchanges': 'Gestion des échanges',
    'admin.title.import': 'Import de Contacts',
    'admin.title.admin': 'Admin',
    'admin.openMenu': 'Ouvrir le menu',

    // Dashboard filters
    'dashboard.filter': 'Filtrer',
    'dashboard.dateStart': 'Date début',
    'dashboard.dateEnd': 'Date fin',
    'dashboard.product': 'Produit',
    'dashboard.optional': '(optionnel)',
    'dashboard.allProductsOption': '-- Tous les produits --',
    'dashboard.selectProduct': 'Sélectionner un produit',
    'dashboard.selectProductHint': '* Veuillez sélectionner un produit pour voir les variantes',

    // Dashboard overview
    'dashboard.ordersToday': "Commandes aujourd'hui",
    'dashboard.ordersWeek': 'Commandes cette semaine',
    'dashboard.ordersMonth': 'Commandes ce mois-ci',
    'dashboard.totalRevenue': 'Revenus totaux',
    'dashboard.order': 'Commande',
    'dashboard.ordersUnit': 'Commandes',
    'dashboard.orderTracking': 'Suivi de commande',
    'dashboard.orderTrackingInfo': 'Livrée : confirmée, prête à expédier, expédiée, livrée, payée. Retournée : retour, retour reçu. Toutes les commandes depuis le début.',
    'dashboard.delivered': 'Livrée',
    'dashboard.returned': 'Retournée',
    'dashboard.orders': 'Commandes',
    'dashboard.ordersInfo': 'Commandes hors annulées, abandonnées et en attente. Montant = total TTC - frais de livraison.',
    'dashboard.last10Days': 'Nombre de commandes au cours des 10 derniers jours',
    'dashboard.orderTraffic': 'Trafic des commandes',
    'dashboard.orderTrafficInfo': 'Confirmée : confirmée, prête à expédier, expédiée, livrée, payée. Abandonnée : panier abandonné. Toutes les commandes depuis le début.',
    'dashboard.confirmed': 'Confirmée',
    'dashboard.abandoned': 'Abandonnée',
    'dashboard.total': 'Total',
    'dashboard.platforms': 'Commandes par plateforme',
    'dashboard.platformsCaption': 'Nombre de commandes et montant (TTC - frais de livraison) par plateforme',
    'dashboard.noPlatform': 'Sans plateforme',
    'dashboard.period.today': "Aujourd'hui",
    'dashboard.period.week': 'Semaine',
    'dashboard.period.month': 'Mois',
    'dashboard.period.all': 'Tout',
    'dashboard.noData': 'Aucune donnée',
    'dashboard.loadError': 'Impossible de charger les statistiques',
    'dashboard.noStore': 'Aucune boutique sélectionnée : créez une boutique puis sélectionnez-la dans le menu.',
    'dashboard.retry': 'Réessayer',
    'dashboard.detailedAnalysis': 'Analyse détaillée',
    'dashboard.detailedAnalysisHint': 'Statistiques par période, produit et région',

    // Statistics pages
    'admin.nav.statistics': 'Statistiques',
    'admin.nav.statsDelivery': 'Livraison',
    'admin.nav.statsProducts': 'Produit',
    'admin.title.statsDelivery': 'Statistiques de livraison',
    'admin.title.statsProducts': 'Statistiques des produits',
    'stats.deliveryCompany': 'Société de livraison',
    'stats.allDeliveryCompanies': 'Toutes les sociétés de livraison',
    'stats.periodFrom': 'Statistiques des commandes du',
    'stats.periodTo': 'au',
    'stats.invalidPeriod': 'La date de début doit être antérieure à la date de fin',
    'stats.inDepot': 'Commandes en dépôt',
    'stats.inDepotInfo': 'Commandes confirmées, pas encore prêtes à expédier.',
    'stats.inTransit': 'En transit',
    'stats.inTransitInfo': 'Commandes prêtes à expédier ou expédiées.',
    'stats.delivered': 'Commandes livrées',
    'stats.deliveredInfo': 'Commandes livrées ou payées.',
    'stats.returned': 'Commandes retournées',
    'stats.returnedInfo': 'Commandes en retour ou retour reçu. Perte = nombre de retours × montant du retour (paramètres).',
    'stats.exchanges': 'Échanges',
    'stats.exchangesInfo': "Commandes d'échange de la période (hors annulées). Coût = retour de la commande d'origine + livraison quand l'échange est gratuit.",
    'stats.expectedProfit': 'Profit prévu',
    'stats.totalValue': 'Valeur totale',
    'stats.profit': 'Profit',
    'stats.revenue': "Chiffre d'affaires",
    'stats.loss': 'Perte',
    'stats.deliveryCost': 'Coût de livraison',
    'stats.avgDeliveryTime': 'Temps moyen de livraison',
    'stats.avgDeliveryTimeInfo': "Temps moyen entre « prête à expédier » et « livrée », d'après l'historique des commandes.",
    'stats.ordersMeasured': 'commandes mesurées',
    'stats.deliveryPerformance': 'Performance de livraison',
    'stats.deliveryPerformanceInfo': 'Livrée : confirmée, prête à expédier, expédiée, livrée, payée. Retournée : retour, retour reçu.',
    'stats.product': 'Produit',
    'stats.selectProduct': 'Sélectionner un produit',
    'stats.searchProduct': 'Rechercher un produit…',
    'stats.noProducts': 'Aucun produit dans cette boutique',
    'stats.noProductMatch': 'Aucun produit trouvé',
    'stats.productPeriodPrefix': 'Statistiques de',
    'stats.productPeriodMiddle': 'sur les commandes du',
    'stats.totalUnits': 'Unités totales',
    'stats.totalUnitsInfo': 'Toutes les unités commandées de ce produit sur la période (en attente + confirmées + rejetées).',
    'stats.pendingUnits': 'Unités en attente',
    'stats.pendingUnitsInfo': 'Commandes en attente de confirmation.',
    'stats.confirmedUnits': 'Unités confirmées',
    'stats.confirmedUnitsInfo': 'Tous les états sauf annulée, abandonnée, en attente et déchet.',
    'stats.rejectedUnits': 'Unités rejetées',
    'stats.rejectedUnitsInfo': 'Commandes annulées, abandonnées ou en déchet.',
    'stats.depotUnits': 'Unités en dépôt',
    'stats.transitUnits': 'Unités en transit',
    'stats.deliveredUnits': 'Unités livrées',
    'stats.returnedUnits': 'Unités retournées',
    'stats.returnedUnitsInfo': 'Commandes en retour ou retour reçu.',
    'stats.exchangedUnitsInfo': "Unités renvoyées pour un échange. Taux = unités échangées / unités livrées.",
    'stats.value': 'Valeur',
    'stats.exchangeRate': "Taux d'échange",
    'stats.performance': 'Performance',
    'stats.performanceInfo': 'Unités confirmées par rapport aux unités rejetées (annulées, abandonnées, déchet).',
    'stats.confirmed': 'Confirmée',
    'stats.rejected': 'Rejetée',
    'stats.deliveryRate': 'Taux de livraison',
    'stats.deliveryRateInfo': 'Unités livrées (livrée, payée) par rapport aux unités retournées (retour, retour reçu).',
    'stats.profitVsRevenue': 'Profit vs Revenus',
    'stats.profitVsRevenueInfo': 'Revenus, profit et unités livrées (livrée, payée), groupés par date de commande.',
    'stats.revenues': 'Revenus',
    'stats.units': 'unités',
    'stats.byDay': 'Par jour',
    'stats.byMonth': 'Par mois',

    // Confirmatrices
    'admin.nav.confirmatrices': 'Confirmatrices',
    'admin.nav.statsConfirmatrices': 'Confirmatrice',
    'admin.title.confirmatrices': 'Gestion des confirmatrices',
    'admin.title.statsConfirmatrices': 'Statistiques des confirmatrices',
    'confirmatrices.subtitle': 'Comptes qui confirment les commandes : ils ne voient que les menus Commandes et Produits.',
    'confirmatrices.add': 'Ajouter une confirmatrice',
    'confirmatrices.edit': 'Modifier la confirmatrice',
    'confirmatrices.name': 'Nom',
    'confirmatrices.firstName': 'Prénom',
    'confirmatrices.lastName': 'Nom',
    'confirmatrices.userName': "Nom d'utilisateur",
    'confirmatrices.password': 'Mot de passe',
    'confirmatrices.newPassword': 'Nouveau mot de passe',
    'confirmatrices.passwordKeep': 'Laisser vide pour garder le mot de passe actuel',
    'confirmatrices.phone': 'Téléphone',
    'confirmatrices.email': 'Email',
    'confirmatrices.status': 'Statut',
    'confirmatrices.active': 'Active',
    'confirmatrices.inactive': 'Désactivée',
    'confirmatrices.activeHint': 'Une confirmatrice désactivée ne peut plus se connecter.',
    'confirmatrices.actions': 'Actions',
    'confirmatrices.empty': 'Aucune confirmatrice pour le moment.',
    'confirmatrices.required': "Le nom d'utilisateur et le mot de passe sont obligatoires",
    'confirmatrices.saved': 'Confirmatrice enregistrée',
    'confirmatrices.deleted': 'Confirmatrice supprimée',
    'confirmatrices.saveError': "Impossible d'enregistrer la confirmatrice",
    'confirmatrices.deleteError': 'Impossible de supprimer la confirmatrice',
    'confirmatrices.loadError': 'Impossible de charger les confirmatrices',
    'stats.confirmatrice': 'Confirmatrice',
    'stats.allConfirmatrices': 'Toutes les confirmatrices',
    'stats.confirmatricePeriodPrefix': 'Commandes confirmées par',
    'stats.confirmatricePeriodMiddle': 'parmi les commandes du',
    'stats.confirmedOrders': 'Commandes confirmées',
    'stats.confirmedOrdersInfo': 'Commandes passées à « confirmée » par la confirmatrice, quel que soit leur état actuel.',
    'stats.ordersInProgress': 'En cours',
    'stats.ordersInProgressInfo': 'Confirmées, prêtes à expédier ou expédiées : pas encore livrées ni retournées.',
    'stats.deliveredOrders': 'Livrées',
    'stats.deliveredOrdersInfo': 'Livrées ou payées.',
    'stats.returnedOrders': 'Retournées',
    'stats.returnedOrdersInfo': 'Retour ou retour reçu.',
    'stats.canceledOrders': 'Annulées',
    'stats.canceledOrdersInfo': 'Annulées après la confirmation.',
    'stats.shareOfConfirmed': 'Part des confirmées',
    'stats.deliveredRevenue': 'Chiffre d\'affaires livré',
    'stats.confirmatriceDeliveryRateInfo': 'Commandes livrées par rapport aux commandes retournées, parmi celles confirmées par la confirmatrice.',
    'stats.ranking': 'Classement des confirmatrices',
    'stats.rankingInfo': 'Cliquez sur une ligne pour voir le détail de la confirmatrice.',
    'activity.title': 'Activité',
    'activity.info': "Tout ce que fait chaque utilisateur sur les commandes et les échanges : création, modification, changement d'état, commentaire, appel, adresse, échange… action par action.",
    'activity.refresh': 'Actualiser',
    'activity.user': 'Utilisateur',
    'activity.allUsers': 'Tous les utilisateurs',
    'activity.admin': 'admin',
    'activity.today': "Aujourd'hui",
    'activity.yesterday': 'Hier',
    'activity.kind': 'Action',
    'activity.allKinds': 'Toutes les actions',
    'activity.search': 'Recherche',
    'activity.searchPlaceholder': 'Référence, valeur, détail…',
    'activity.when': 'Heure',
    'activity.order': 'Commande',
    'activity.detail': 'Détail',
    'activity.empty': 'Aucune action le',
    'activity.loadError': "Impossible de charger l'activité",
    'activity.confirmation': 'Confirmation',
    'activity.status': "Changement d'état",
    'activity.creation': 'Création commande',
    'activity.edit': 'Modification commande',
    'activity.comment': 'Commentaire',
    'activity.review': 'Avis client',
    'activity.address': 'Adresse',
    'activity.city': 'Ville',
    'activity.call': 'Appel',
    'activity.variant': 'Variante',
    'activity.deletion': 'Suppression commande',
    'activity.shipping': 'Envoi à la livraison',
    'activity.tracking': 'Suivi livraison',
    'activity.print': 'Impression',
    'activity.packing': 'Emballage',
    'activity.paymentImport': 'Import paiement',
    'activity.exchange': 'Échange créé',
    'activity.exchangeDeletion': 'Échange supprimé',
    'notifications.newOrder': 'Nouvelle commande',
    'stats.noConfirmatrices': 'Aucune confirmatrice : ajoutez-en dans le menu Confirmatrices.',

    // Facebook Ads
    'admin.nav.facebookStats': 'Statistiques Facebook',
    'admin.title.facebookStats': 'Statistiques Facebook',
    'facebook.title': 'Facebook Ads',
    'facebook.subtitle': 'Connectez le compte Facebook qui gère vos publicités pour suivre vos campagnes et leurs dépenses.',
    'facebook.notConfigured': "L'application Facebook n'est pas encore configurée sur le serveur (variables Facebook__AppId et Facebook__AppSecret).",
    'facebook.permissions': 'Accès en lecture seule à vos comptes publicitaires, à vos pages, leurs publications et leurs commentaires.',
    'facebook.connect': 'Connecter Facebook',
    'facebook.reconnect': 'Reconnecter',
    'facebook.disconnect': 'Déconnecter',
    'facebook.disconnectConfirm': 'Déconnecter Facebook pour cette boutique ?',
    'facebook.notConnected': 'Aucun compte Facebook connecté pour cette boutique.',
    'facebook.connectedAs': 'Connecté en tant que',
    'facebook.connectedOn': 'Connecté le',
    'facebook.validUntil': "Accès valable jusqu'au",
    'facebook.days': 'jours',
    'facebook.permanentAccess': 'Accès permanent (n’expire jamais)',
    'facebook.autoRenewHint': 'Renouvelé tout seul avant la fin quand un administrateur ouvre l’administration avec ce compte Facebook connecté dans le navigateur.',
    'facebook.expired': "L'accès a expiré : reconnectez Facebook.",
    'facebook.connected': 'Facebook connecté',
    'facebook.disconnected': 'Facebook déconnecté',
    'facebook.cancelled': 'Connexion Facebook annulée',
    'facebook.sdkError': 'Impossible de charger Facebook. Vérifiez votre connexion internet.',
    'facebook.connectError': 'Impossible de connecter Facebook',
    'facebook.daysRecovered': 'jour(s) récupéré(s) dans les workers',
    'facebook.reconnectTitle': 'La connexion Facebook a expiré',
    'facebook.reconnectRefusedTitle': 'Facebook refuse la connexion',
    'facebook.reconnectWorkersNote': 'Reconnectez Facebook pour que les workers et les statistiques reçoivent à nouveau les données. Les jours que les workers n’ont pas pu récupérer seront revérifiés automatiquement.',
    'facebook.reconnectNow': 'Reconnecter Facebook',
    'facebook.later': 'Plus tard',
    'facebook.expiredOn': 'L’accès Facebook a pris fin le',
    'facebook.seeStats': 'Voir les statistiques',
    'facebook.loadError': 'Impossible de charger les campagnes Facebook',
    'facebook.notConnectedStats': 'Connectez votre compte Facebook dans Paramétrage pour voir vos campagnes et leurs dépenses.',
    'facebook.goToSettings': 'Aller à Paramétrage',
    'facebook.thisMonth': 'Ce mois',
    'facebook.last30': '30 derniers jours',
    'facebook.lifetime': 'Depuis le début',
    'facebook.search': 'Rechercher une campagne ou un compte…',
    'facebook.allAccounts': 'Tous les comptes',
    'facebook.searchAccount': 'Rechercher un compte…',
    'facebook.noAccountFound': 'Aucun compte trouvé',
    'facebook.show': 'Afficher les statistiques',
    'facebook.showHint': 'Choisissez le compte publicitaire et la période, puis cliquez sur « Afficher les statistiques » : les chiffres sont lus chez Facebook à ce moment-là.',
    'facebook.filtersChanged': 'Les filtres ont changé : cliquez sur « Afficher les statistiques » pour mettre à jour.',
    'facebook.loadingAccounts': 'Chargement des comptes…',
    'facebook.accountInactive': 'inactif',
    'facebook.previous': 'Précédent',
    'facebook.next': 'Suivant',
    'facebook.of': 'sur',
    'facebook.refresh': 'Actualiser',
    'facebook.refreshHint': 'Relire les chiffres chez Facebook (sinon ceux des 5 dernières minutes)',
    'facebook.warnings': "Certains comptes publicitaires n'ont pas pu être lus :",
    'facebook.totalSpend': 'Dépense totale',
    'facebook.campaigns': 'Campagnes',
    'facebook.activeCampaigns': 'actives',
    'facebook.impressions': 'Impressions',
    'facebook.clicks': 'Clics',
    'facebook.spendByCampaign': 'Dépenses par campagne',
    'facebook.spendByCampaignInfo': 'Les 10 campagnes qui ont le plus dépensé sur la période.',
    'facebook.allCampaigns': 'Toutes les campagnes',
    'facebook.campaign': 'Campagne',
    'facebook.page': 'Page',
    'facebook.adAccount': 'Compte publicitaire',
    'facebook.status': 'Statut',
    'facebook.spend': 'Dépensé',
    'facebook.noCampaigns': 'Aucune campagne trouvée.',
    'facebook.statusActive': 'Active',
    'facebook.statusPaused': 'En pause',
    'facebook.statusArchived': 'Archivée',
    'facebook.statusDeleted': 'Supprimée',
    'facebook.missingRate': "Taux du dollar non configuré : les dépenses en USD ne peuvent pas être converties en TND.",
    'admin.nav.facebook': 'Facebook',
    'admin.nav.facebookWorkers': 'Facebook Ads Workers',
    'admin.title.facebookWorkers': 'Facebook Ads Workers',
    'dollarRate.title': 'Taux du dollar',
    'dollarRate.subtitle': 'Convertit les dépenses Facebook Ads (en dollars) en dinars : Statistiques Facebook et Facebook Ads Workers.',
    'dollarRate.label': 'Taux de change',
    'dollarRate.save': 'Enregistrer',
    'dollarRate.saved': 'Taux du dollar enregistré',
    'dollarRate.saveError': "Impossible d'enregistrer le taux",
    'dollarRate.invalid': 'Le taux doit être un nombre supérieur à 0',
    'dollarRate.example': 'Exemple :',
    'dollarRate.notSet': 'Aucun taux configuré : les dépenses restent affichées en dollars.',
    'workers.intro': 'Chaque nuit à 2h00, chaque worker crée une charge « Dollar » sur son produit.',
    'workers.introDetail': "Montant = dépense de la veille de la campagne Facebook × taux du dollar (Paramétrage).",
    'workers.add': 'Nouveau worker',
    'workers.edit': 'Modifier le worker',
    'workers.notConnected': 'Aucun compte Facebook connecté pour cette boutique : connectez Facebook dans Paramétrage.',
    'workers.missingRate': "Taux du dollar non configuré : les workers ne peuvent pas convertir les dépenses en TND.",
    'workers.loadError': 'Impossible de charger les workers',
    'workers.activeWorkers': 'Workers actifs',
    'workers.total': 'au total',
    'workers.totalCreated': 'Charges créées',
    'workers.nextRun': 'Prochaine exécution',
    'workers.nextRunHint': 'Dépense de la veille',
    'workers.empty': 'Aucun worker. Créez-en un pour transformer chaque jour la dépense d\'une campagne en charge sur un produit.',
    'workers.active': 'Actif',
    'workers.paused': 'En pause',
    'workers.pause': 'Mettre en pause',
    'workers.resume': 'Réactiver',
    'workers.lastRun': 'Dernière exécution',
    'workers.neverRun': 'Pas encore exécuté',
    'workers.run': 'Exécuter',
    'workers.history': 'Historique',
    'workers.chooseCampaign': 'Choisissez une campagne',
    'workers.chooseProduct': 'Choisissez un produit',
    'workers.created': 'Worker créé',
    'workers.updated': 'Worker modifié',
    'workers.saveError': "Impossible d'enregistrer le worker",
    'workers.deleted': 'Worker supprimé (les charges déjà créées sont conservées)',
    'workers.expenseCreated': 'Charge créée',
    'workers.runError': "Impossible d'exécuter le worker",
    'workers.noSpend': 'Aucune dépense ce jour-là',
    'workers.campaignsError': 'Impossible de charger les campagnes Facebook',
    'workers.searchCampaign': 'Rechercher une campagne…',
    'workers.loadingCampaigns': 'Chargement des campagnes…',
    'workers.campaign': 'Campagne Facebook',
    'workers.product': 'Produit',
    'workers.productHint': 'La charge « Dollar » de chaque jour est créée sur ce produit.',
    'workers.activeHint': 'En pause, le worker ne crée aucune charge ; réactivé, il rattrape les jours manquants de sa période.',
    'workers.formHelp': "Chaque nuit à 2h00, la dépense de la veille de cette campagne (en USD) est multipliée par le taux du dollar et enregistrée comme charge « Dollar » datée de la veille.",
    'workers.runTitle': 'Exécuter maintenant',
    'workers.recheck': 'Revérifier',
    'workers.recheckTitle': 'Revérifier toutes les dépenses',
    'workers.recheckHelp': "Relit chez Facebook la dépense de chaque jour de la période : les jours manquants ou en erreur sont ajoutés, un montant qui a changé chez Facebook est corrigé et la charge d'un jour sans dépense est supprimée. Un jour déjà comptabilisé garde le taux du dollar avec lequel il a été compté.",
    'workers.recheckError': 'Impossible de revérifier ce worker',
    'workers.alertTitleOne': 'Worker Facebook en erreur',
    'workers.alertTitleMany': 'workers Facebook en erreur',
    'workers.alertIntro': 'Ces jours ne sont pas ajoutés aux charges tant que l’erreur reste. Le worker réessaie toutes les heures.',
    'workers.alertDays': 'jour(s) non facturé(s) depuis le',
    'workers.alertSee': 'Voir les workers',
    'workers.daysChecked': 'jours vérifiés',
    'workers.upToDate': 'tout est à jour',
    'workers.chargesAdded': 'charge(s) ajoutée(s)',
    'workers.chargesFixed': 'corrigée(s)',
    'workers.chargesRemoved': 'supprimée(s)',
    'workers.difference': 'écart',
    'workers.yesterday': 'hier',
    'workers.loadingMore': 'Chargement…',
    'workers.scrollForMore': 'Faites défiler pour voir la suite',
    'workers.campaignsLabel': 'campagnes',
    'workers.refreshCampaigns': 'Actualiser',
    'workers.runHelp': "Crée la charge d'un jour passé (par exemple un jour manqué). Un jour déjà comptabilisé n'est jamais créé deux fois.",
    'workers.day': 'Jour',
    'workers.close': 'Fermer',
    'workers.noHistory': 'Aucune exécution pour le moment.',
    'workers.status': 'Statut',
    'workers.amount': 'Montant',
    'workers.runAt': 'Exécuté le',
    'workers.statusSuccess': 'Charge créée',
    'workers.statusNoSpend': 'Aucune dépense',
    'workers.statusError': 'Erreur',
    'workers.startDate': 'Date de début',
    'workers.endDate': 'Date de fin',
    'workers.endDateHint': 'Vide = sans fin',
    'workers.chooseStart': 'Choisissez la date de début',
    'workers.endBeforeStart': 'La date de fin doit être après la date de début',
    'workers.pastStartHint': "Date de début passée : les charges de tous les jours depuis cette date sont créées tout de suite à l'enregistrement.",
    'workers.daysCaughtUp': 'jour(s) rattrapé(s)',
    'workers.period': 'Période',
    'workers.noEnd': 'sans fin',
    'workers.daysDone': 'jour(s) traité(s)',
    'workers.finished': 'Terminé',
    'facebook.reach': 'Portée',
    'facebook.frequency': 'Fréq.',
    'facebook.ctr': 'CTR',
    'facebook.cpc': 'CPC',
    'facebook.cpcHint': 'Coût par clic',
    'facebook.cpm': 'CPM',
    'facebook.cpmHint': 'Coût pour 1 000 impressions',
    'facebook.purchases': 'Achats',
    'facebook.purchaseShort': 'achat',
    'facebook.costPerPurchase': 'Coût/achat',
    'facebook.roas': 'ROAS',
    'facebook.messages': 'Messages',
    'facebook.costPerMessage': 'Coût/message',
    'facebook.leads': 'Leads',

    // Orders filters
    'orders.filters.refine': 'Affiner la recherche',
    'orders.filters.activeLabel': 'filtre(s) actif(s)',
    'orders.filters.clearTitle': 'Effacer les filtres',
    'orders.filters.hide': 'Masquer',
    'orders.filters.search': 'Recherche',
    'orders.filters.searchPlaceholder': 'ID, nom, téléphone...',
    'orders.filters.platform': 'Plateforme',
    'orders.filters.allFem': 'Toutes',
    'orders.filters.sync': 'Synchronisation',
    'orders.filters.synced': 'Synchronisé',
    'orders.filters.notSynced': 'Non synchronisé',
    'orders.filters.attemptReserved': 'Tentative d\'appel',
    'orders.filters.reserved': 'Avec tentative',
    'orders.filters.notReserved': 'Sans tentative',
    'orders.filters.exchange': 'Échange',
    'orders.filters.notExchange': 'Non échange',
    'orders.filters.printed': 'Impression',
    'orders.filters.notPrinted': 'Non imprimées',
    'orders.filters.printedOnly': 'Imprimées',
    'orders.printed': 'Imprimée',
    'orders.printedTitle': "L'étiquette de cette commande a déjà été imprimée",
    'orders.filters.packed': 'Emballage',
    'orders.filters.notPacked': 'Non emballées',
    'orders.filters.packedOnly': 'Emballées',
    'orders.packed': 'Emballée',
    'orders.packedTitle': 'Le colis de cette commande a été emballé (code-barres scanné)',
    'orders.selection.count': 'commande(s) sélectionnée(s)',
    'orders.selection.printedCount': 'déjà imprimée(s)',
    'orders.selection.excludePrinted': 'Exclure les imprimées',
    'orders.selection.excludedPrinted': 'commande(s) déjà imprimée(s) retirée(s) de la sélection',
    'orders.selection.clear': 'Tout désélectionner',
    'orders.filters.products': 'Produits',
    'orders.filters.allProducts': 'Tous les produits',
    'orders.filters.searchProduct': 'Rechercher un produit...',
    'orders.filters.noProduct': 'Aucun produit trouvé',
    'orders.filters.deselectAll': 'Tout désélectionner',

    // Orders header / toolbar
    'orders.header.title': 'Commandes',
    'orders.header.newOrder': 'Nouvelle Commande',
    'orders.header.printAll': 'Imprimer tous',
    'orders.header.printSelected': 'Imprimer sélection',
    'orders.header.printStock': 'Imprimer Stock',
    'orders.header.pay': 'Payer',
    'orders.header.syncAll': 'Synchroniser Tout',
    'orders.header.syncSelected': 'Sync Sélection',
    'orders.header.syncingTitle': 'Commande en cours de synchronisation',
    'orders.header.syncingWait': 'Veuillez patienter, ne fermez pas cette fenêtre.',
    'orders.header.order': 'Commande',
    'orders.header.exportNote': "Remarque : L'export inclura uniquement les commandes actuellement filtrées",
    'orders.header.ordersWord': 'commandes',
    'orders.modal.sync': 'Synchroniser',
    'orders.modal.print': 'Imprimer',
    'orders.modal.track': 'Suivre',
    'orders.modal.import': 'Importer',
    'orders.modal.syncTitle': 'Confirmer la synchronisation',
    'orders.modal.printTitle': "Confirmer l'impression",
    'orders.modal.trackTitle': 'Confirmer le suivi',
    'orders.modal.importTitle': 'Importer des commandes',
    'orders.modal.allOrdersWord': 'toutes les commandes',
    'orders.modal.allLabelsWord': 'toutes les étiquettes',
    'orders.modal.ordersSelectedQ': 'commandes sélectionnées ?',
    'orders.modal.labelsSelectedQ': 'étiquettes sélectionnées ?',
    'orders.modal.syncDesc': 'Cette action synchronisera les commandes avec le système externe. Voulez-vous continuer?',
    'orders.modal.printDesc': "Cette action générera un document d'impression avec une étiquette par page. Voulez-vous continuer?",
    'orders.modal.trackDesc': 'Cette action permettra de suivre les commandes sélectionnées. Voulez-vous continuer?',
    'orders.modal.syncWarnSelected': 'Seules les commandes sélectionnées seront synchronisées.',
    'orders.modal.syncWarnAll': 'Toutes les commandes actuellement filtrées seront synchronisées.',
    'orders.modal.printWarnSelected': 'Seules les commandes sélectionnées seront imprimées.',
    'orders.modal.printWarnAll': 'Toutes les commandes actuellement filtrées seront imprimées.',
    'orders.modal.trackWarnSelected': 'Seules les commandes sélectionnées seront suivies.',
    'orders.modal.trackWarnAll': 'Toutes les commandes actuellement filtrées seront suivies.',
    'orders.modal.importSelectFile': 'Sélectionner un fichier Excel',
    'orders.modal.importPreview': 'Aperçu des codes-barres',
    'orders.modal.importPreviewFirst': 'premiers',
    'orders.modal.importHint': 'Le fichier Excel doit contenir une colonne nommée "barCode" avec les codes-barres des commandes à importer.',
    'orders.delete.title': 'Supprimer la commande',
    'orders.delete.q1': 'Êtes-vous sûr de vouloir supprimer la commande',
    'orders.delete.q2': '? Cette action est irréversible.',
    'status.allOrders': 'Toutes les commandes',
    'status.abandoned': 'Panier abandonné',
    'status.pending': 'En attente',
    'status.confirmed': 'Confirmées',
    'status.readyToShip': 'Prêt à expédier',
    'status.shipped': 'Expédiées',
    'status.delivered': 'Livrées',
    'status.canceled': 'Annulées',
    'status.return': 'Retour',
    'status.returnReceived': 'Retour reçu',
    'status.dechet': 'Déchet',
    'status.paid': 'Payé',
    'orders.table.selection': 'Sélection',
    'orders.table.idRef': 'ID / Référence',
    'orders.table.client': 'Client',
    'orders.table.totalTTC': 'Total TTC',
    'orders.table.comment': 'Commentaire',
    'orders.table.clientReview': 'Avis Client',
    'orders.table.syncro': 'Syncro',
    'orders.table.callReserved': 'Tentative d\'appel',
    'orders.table.statusBtn': 'Statut',
    'orders.table.changeStatusTitle': 'Changer le statut des commandes sélectionnées',
    'orders.table.selectOrdersTitle': 'Veuillez sélectionner des commandes',
    'orders.table.changeStatus': 'Changer le statut',
    'orders.table.selectedFem': 'sélectionnées',

    // Common admin actions / labels
    'common.search': 'Rechercher',
    'common.retry': 'Réessayer',
    'common.add': 'Ajouter',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.close': 'Fermer',
    'common.confirm': 'Confirmer',
    'common.apply': 'Appliquer',
    'common.reset': 'Réinitialiser',
    'common.filter': 'Filtrer',
    'common.filters': 'Filtres',
    'common.clear': 'Effacer',
    'common.export': 'Exporter',
    'common.import': 'Importer',
    'common.print': 'Imprimer',
    'common.actions': 'Actions',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.total': 'Total',
    'common.name': 'Nom',
    'common.price': 'Prix',
    'common.quantity': 'Quantité',
    'common.loading': 'Chargement...',
    'common.noData': 'Aucune donnée',
    'common.all': 'Tous',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.details': 'Détails',
  },
  ar: {
    'nav.home': 'الرئيسية',
    'nav.administration': 'الإدارة',
    'nav.parametrage': 'الإعدادات',
    'nav.documentation': 'التوثيق',
    'nav.login': 'تسجيل الدخول',
    'nav.logout': 'تسجيل الخروج',
    'nav.profile': 'الملف الشخصي',
    'nav.cart': 'السلة',
    'nav.language': 'اللغة',

    // Admin sidebar navigation
    'admin.nav.dashboard': 'لوحة التحكم',
    'admin.nav.products': 'المنتجات',
    'admin.nav.allProducts': 'كل المنتجات',
    'admin.nav.variants': 'المتغيرات',
    'admin.nav.categories': 'الفئات',
    'admin.nav.brands': 'العلامات التجارية',
    'admin.nav.suppliers': 'الموردون',
    'admin.nav.orders': 'الطلبات',
    'admin.nav.allOrders': 'كل الطلبات',
    'admin.nav.deliveryCompany': 'شركة التوصيل',
    'admin.nav.exchange': 'الاستبدال',
    'admin.nav.charges': 'المصاريف',
    'admin.nav.barcodeVerification': 'التحقق من الباركود',
    'admin.nav.returnReceived': 'المرتجع المستلم',
    'admin.nav.purchases': 'المشتريات',
    'admin.nav.purchasesList': 'قائمة المشتريات',
    'admin.nav.supplierPayment': 'تسوية المورد',
    'admin.nav.supplierConsumption': 'استهلاك المورد',
    'admin.nav.smsing': 'الرسائل القصيرة',
    'admin.nav.promoCodes': 'رموز الخصم',
    'admin.nav.socialLeads': 'عملاء وسائل التواصل',
    'admin.nav.settings': 'الإعدادات',

    // Admin page titles (TopBar)
    'admin.title.dashboard': 'لوحة التحكم',
    'admin.title.products': 'إدارة المنتجات',
    'admin.title.variants': 'إدارة المتغيرات',
    'admin.title.brands': 'إدارة العلامات التجارية',
    'admin.title.suppliers': 'إدارة الموردين',
    'admin.title.orders': 'إدارة الطلبات',
    'admin.title.deliveries': 'إدارة عمليات التوصيل',
    'admin.title.customers': 'إدارة العملاء',
    'admin.title.settings': 'الإعدادات',
    'admin.title.purchases': 'إدارة المشتريات',
    'admin.title.exchanges': 'إدارة عمليات الاستبدال',
    'admin.title.import': 'استيراد جهات الاتصال',
    'admin.title.admin': 'الإدارة',
    'admin.openMenu': 'فتح القائمة',

    // Dashboard filters
    'dashboard.filter': 'تصفية',
    'dashboard.dateStart': 'تاريخ البداية',
    'dashboard.dateEnd': 'تاريخ النهاية',
    'dashboard.product': 'المنتج',
    'dashboard.optional': '(اختياري)',
    'dashboard.allProductsOption': '-- كل المنتجات --',
    'dashboard.selectProduct': 'اختر منتجًا',
    'dashboard.selectProductHint': '* يرجى اختيار منتج لعرض المتغيرات',

    // Dashboard overview
    'dashboard.ordersToday': 'طلبات اليوم',
    'dashboard.ordersWeek': 'طلبات هذا الأسبوع',
    'dashboard.ordersMonth': 'طلبات هذا الشهر',
    'dashboard.totalRevenue': 'إجمالي الإيرادات',
    'dashboard.order': 'طلب',
    'dashboard.ordersUnit': 'طلبات',
    'dashboard.orderTracking': 'تتبع الطلبات',
    'dashboard.orderTrackingInfo': 'تم التوصيل: مؤكد، جاهز للشحن، تم الشحن، تم التوصيل، مدفوع. مرتجع: إرجاع، تم استلام الإرجاع. كل الطلبات منذ البداية.',
    'dashboard.delivered': 'تم التوصيل',
    'dashboard.returned': 'مرتجع',
    'dashboard.orders': 'الطلبات',
    'dashboard.ordersInfo': 'الطلبات باستثناء الملغاة والمتروكة والمعلقة. المبلغ = الإجمالي مع الضريبة - مصاريف التوصيل.',
    'dashboard.last10Days': 'عدد الطلبات خلال آخر 10 أيام',
    'dashboard.orderTraffic': 'حركة الطلبات',
    'dashboard.orderTrafficInfo': 'مؤكد: مؤكد، جاهز للشحن، تم الشحن، تم التوصيل، مدفوع. متروك: سلة متروكة. كل الطلبات منذ البداية.',
    'dashboard.confirmed': 'مؤكد',
    'dashboard.abandoned': 'متروك',
    'dashboard.total': 'المجموع',
    'dashboard.platforms': 'الطلبات حسب المنصة',
    'dashboard.platformsCaption': 'عدد الطلبات والمبلغ (الإجمالي - مصاريف التوصيل) حسب المنصة',
    'dashboard.noPlatform': 'بدون منصة',
    'dashboard.period.today': 'اليوم',
    'dashboard.period.week': 'الأسبوع',
    'dashboard.period.month': 'الشهر',
    'dashboard.period.all': 'الكل',
    'dashboard.noData': 'لا توجد بيانات',
    'dashboard.loadError': 'تعذر تحميل الإحصائيات',
    'dashboard.noStore': 'لم يتم اختيار أي متجر: أنشئ متجرًا ثم اختره من القائمة.',
    'dashboard.retry': 'إعادة المحاولة',
    'dashboard.detailedAnalysis': 'تحليل مفصل',
    'dashboard.detailedAnalysisHint': 'إحصائيات حسب الفترة والمنتج والمنطقة',

    // Statistics pages
    'admin.nav.statistics': 'الإحصائيات',
    'admin.nav.statsDelivery': 'التوصيل',
    'admin.nav.statsProducts': 'المنتج',
    'admin.title.statsDelivery': 'إحصائيات التوصيل',
    'admin.title.statsProducts': 'إحصائيات المنتجات',
    'stats.deliveryCompany': 'شركة التوصيل',
    'stats.allDeliveryCompanies': 'كل شركات التوصيل',
    'stats.periodFrom': 'إحصائيات الطلبات من',
    'stats.periodTo': 'إلى',
    'stats.invalidPeriod': 'يجب أن يكون تاريخ البداية قبل تاريخ النهاية',
    'stats.inDepot': 'طلبات في المستودع',
    'stats.inDepotInfo': 'طلبات مؤكدة لم تُجهّز للشحن بعد.',
    'stats.inTransit': 'قيد النقل',
    'stats.inTransitInfo': 'طلبات جاهزة للشحن أو تم شحنها.',
    'stats.delivered': 'طلبات تم توصيلها',
    'stats.deliveredInfo': 'طلبات تم توصيلها أو دفعها.',
    'stats.returned': 'طلبات مرتجعة',
    'stats.returnedInfo': 'طلبات قيد الإرجاع أو تم استلام إرجاعها. الخسارة = عدد المرتجعات × مبلغ الإرجاع (الإعدادات).',
    'stats.exchanges': 'الاستبدالات',
    'stats.exchangesInfo': 'طلبات الاستبدال خلال الفترة (باستثناء الملغاة). التكلفة = إرجاع الطلب الأصلي + التوصيل عندما يكون الاستبدال مجانيًا.',
    'stats.expectedProfit': 'الربح المتوقع',
    'stats.totalValue': 'القيمة الإجمالية',
    'stats.profit': 'الربح',
    'stats.revenue': 'رقم المعاملات',
    'stats.loss': 'الخسارة',
    'stats.deliveryCost': 'تكلفة التوصيل',
    'stats.avgDeliveryTime': 'متوسط مدة التوصيل',
    'stats.avgDeliveryTimeInfo': 'متوسط المدة بين «جاهز للشحن» و«تم التوصيل» حسب سجل الطلبات.',
    'stats.ordersMeasured': 'طلبات محسوبة',
    'stats.deliveryPerformance': 'أداء التوصيل',
    'stats.deliveryPerformanceInfo': 'تم التوصيل: مؤكد، جاهز للشحن، تم الشحن، تم التوصيل، مدفوع. مرتجع: إرجاع، تم استلام الإرجاع.',
    'stats.product': 'المنتج',
    'stats.selectProduct': 'اختر منتجًا',
    'stats.searchProduct': 'ابحث عن منتج…',
    'stats.noProducts': 'لا توجد منتجات في هذا المتجر',
    'stats.noProductMatch': 'لم يتم العثور على أي منتج',
    'stats.productPeriodPrefix': 'إحصائيات',
    'stats.productPeriodMiddle': 'للطلبات من',
    'stats.totalUnits': 'إجمالي الوحدات',
    'stats.totalUnitsInfo': 'كل الوحدات المطلوبة من هذا المنتج خلال الفترة (قيد الانتظار + مؤكدة + مرفوضة).',
    'stats.pendingUnits': 'وحدات قيد الانتظار',
    'stats.pendingUnitsInfo': 'طلبات في انتظار التأكيد.',
    'stats.confirmedUnits': 'وحدات مؤكدة',
    'stats.confirmedUnitsInfo': 'كل الحالات باستثناء الملغاة والمتروكة وقيد الانتظار والتالفة.',
    'stats.rejectedUnits': 'وحدات مرفوضة',
    'stats.rejectedUnitsInfo': 'طلبات ملغاة أو متروكة أو تالفة.',
    'stats.depotUnits': 'وحدات في المستودع',
    'stats.transitUnits': 'وحدات قيد النقل',
    'stats.deliveredUnits': 'وحدات تم توصيلها',
    'stats.returnedUnits': 'وحدات مرتجعة',
    'stats.returnedUnitsInfo': 'طلبات قيد الإرجاع أو تم استلام إرجاعها.',
    'stats.exchangedUnitsInfo': 'وحدات أُعيدت للاستبدال. النسبة = الوحدات المستبدلة / الوحدات المسلّمة.',
    'stats.value': 'القيمة',
    'stats.exchangeRate': 'نسبة الاستبدال',
    'stats.performance': 'الأداء',
    'stats.performanceInfo': 'الوحدات المؤكدة مقارنة بالوحدات المرفوضة (ملغاة، متروكة، تالفة).',
    'stats.confirmed': 'مؤكدة',
    'stats.rejected': 'مرفوضة',
    'stats.deliveryRate': 'نسبة التوصيل',
    'stats.deliveryRateInfo': 'الوحدات المسلّمة (تم التوصيل، مدفوع) مقارنة بالوحدات المرتجعة (إرجاع، تم استلام الإرجاع).',
    'stats.profitVsRevenue': 'الربح مقابل الإيرادات',
    'stats.profitVsRevenueInfo': 'الإيرادات والربح والوحدات المسلّمة (تم التوصيل، مدفوع) حسب تاريخ الطلب.',
    'stats.revenues': 'الإيرادات',
    'stats.units': 'وحدات',
    'stats.byDay': 'حسب اليوم',
    'stats.byMonth': 'حسب الشهر',

    // Confirmatrices
    'admin.nav.confirmatrices': 'المؤكِّدات',
    'admin.nav.statsConfirmatrices': 'المؤكِّدة',
    'admin.title.confirmatrices': 'إدارة المؤكِّدات',
    'admin.title.statsConfirmatrices': 'إحصائيات المؤكِّدات',
    'confirmatrices.subtitle': 'حسابات تؤكد الطلبات: لا ترى سوى قائمتي الطلبات والمنتجات.',
    'confirmatrices.add': 'إضافة مؤكِّدة',
    'confirmatrices.edit': 'تعديل المؤكِّدة',
    'confirmatrices.name': 'الاسم',
    'confirmatrices.firstName': 'الاسم',
    'confirmatrices.lastName': 'اللقب',
    'confirmatrices.userName': 'اسم المستخدم',
    'confirmatrices.password': 'كلمة المرور',
    'confirmatrices.newPassword': 'كلمة مرور جديدة',
    'confirmatrices.passwordKeep': 'اتركها فارغة للاحتفاظ بكلمة المرور الحالية',
    'confirmatrices.phone': 'الهاتف',
    'confirmatrices.email': 'البريد الإلكتروني',
    'confirmatrices.status': 'الحالة',
    'confirmatrices.active': 'نشطة',
    'confirmatrices.inactive': 'معطّلة',
    'confirmatrices.activeHint': 'المؤكِّدة المعطّلة لا يمكنها تسجيل الدخول.',
    'confirmatrices.actions': 'إجراءات',
    'confirmatrices.empty': 'لا توجد مؤكِّدات حاليًا.',
    'confirmatrices.required': 'اسم المستخدم وكلمة المرور إلزاميان',
    'confirmatrices.saved': 'تم حفظ المؤكِّدة',
    'confirmatrices.deleted': 'تم حذف المؤكِّدة',
    'confirmatrices.saveError': 'تعذر حفظ المؤكِّدة',
    'confirmatrices.deleteError': 'تعذر حذف المؤكِّدة',
    'confirmatrices.loadError': 'تعذر تحميل المؤكِّدات',
    'stats.confirmatrice': 'المؤكِّدة',
    'stats.allConfirmatrices': 'كل المؤكِّدات',
    'stats.confirmatricePeriodPrefix': 'الطلبات التي أكدتها',
    'stats.confirmatricePeriodMiddle': 'من بين طلبات',
    'stats.confirmedOrders': 'طلبات مؤكدة',
    'stats.confirmedOrdersInfo': 'طلبات حوّلتها المؤكِّدة إلى «مؤكد»، مهما كانت حالتها الحالية.',
    'stats.ordersInProgress': 'قيد المعالجة',
    'stats.ordersInProgressInfo': 'مؤكدة أو جاهزة للشحن أو تم شحنها: لم تُسلّم ولم تُرجع بعد.',
    'stats.deliveredOrders': 'تم توصيلها',
    'stats.deliveredOrdersInfo': 'تم توصيلها أو دفعها.',
    'stats.returnedOrders': 'مرتجعة',
    'stats.returnedOrdersInfo': 'إرجاع أو تم استلام الإرجاع.',
    'stats.canceledOrders': 'ملغاة',
    'stats.canceledOrdersInfo': 'أُلغيت بعد التأكيد.',
    'stats.shareOfConfirmed': 'النسبة من المؤكدة',
    'stats.deliveredRevenue': 'رقم المعاملات المسلّم',
    'stats.confirmatriceDeliveryRateInfo': 'الطلبات المسلّمة مقارنة بالمرتجعة، من بين الطلبات التي أكدتها المؤكِّدة.',
    'stats.ranking': 'ترتيب المؤكِّدات',
    'stats.rankingInfo': 'انقر على سطر لعرض تفاصيل المؤكِّدة.',
    'activity.title': 'النشاط',
    'activity.info': 'كل ما يفعله كل مستخدم على الطلبات والاستبدالات: إنشاء، تعديل، تغيير الحالة، تعليق، مكالمة، عنوان، استبدال… إجراءً بإجراء.',
    'activity.refresh': 'تحديث',
    'activity.user': 'المستخدم',
    'activity.allUsers': 'كل المستخدمين',
    'activity.admin': 'مسؤول',
    'activity.today': 'اليوم',
    'activity.yesterday': 'أمس',
    'activity.kind': 'الإجراء',
    'activity.allKinds': 'كل الإجراءات',
    'activity.search': 'بحث',
    'activity.searchPlaceholder': 'المرجع، القيمة، التفاصيل…',
    'activity.when': 'الساعة',
    'activity.order': 'الطلب',
    'activity.detail': 'التفاصيل',
    'activity.empty': 'لا يوجد أي إجراء يوم',
    'activity.loadError': 'تعذر تحميل النشاط',
    'activity.confirmation': 'تأكيد',
    'activity.status': 'تغيير الحالة',
    'activity.creation': 'إنشاء طلب',
    'activity.edit': 'تعديل طلب',
    'activity.comment': 'تعليق',
    'activity.review': 'رأي الحريف',
    'activity.address': 'العنوان',
    'activity.city': 'المدينة',
    'activity.call': 'مكالمة',
    'activity.variant': 'المتغير',
    'activity.deletion': 'حذف طلب',
    'activity.shipping': 'إرسال للتوصيل',
    'activity.tracking': 'تتبع التوصيل',
    'activity.print': 'طباعة',
    'activity.packing': 'تغليف',
    'activity.paymentImport': 'استيراد الدفع',
    'activity.exchange': 'إنشاء استبدال',
    'activity.exchangeDeletion': 'حذف استبدال',
    'notifications.newOrder': 'طلب جديد',
    'stats.noConfirmatrices': 'لا توجد مؤكِّدات: أضفها من قائمة المؤكِّدات.',

    // Facebook Ads
    'admin.nav.facebookStats': 'إحصائيات فيسبوك',
    'admin.title.facebookStats': 'إحصائيات فيسبوك',
    'facebook.title': 'إعلانات فيسبوك',
    'facebook.subtitle': 'اربط حساب فيسبوك الذي يدير إعلاناتك لمتابعة حملاتك ومصاريفها.',
    'facebook.notConfigured': 'تطبيق فيسبوك غير مُعدّ بعد على الخادم (المتغيران Facebook__AppId و Facebook__AppSecret).',
    'facebook.permissions': 'وصول للقراءة فقط إلى حساباتك الإعلانية وصفحاتك ومنشوراتها وتعليقاتها.',
    'facebook.connect': 'ربط فيسبوك',
    'facebook.reconnect': 'إعادة الربط',
    'facebook.disconnect': 'فصل',
    'facebook.disconnectConfirm': 'فصل فيسبوك عن هذا المتجر؟',
    'facebook.notConnected': 'لا يوجد حساب فيسبوك مرتبط بهذا المتجر.',
    'facebook.connectedAs': 'مرتبط باسم',
    'facebook.connectedOn': 'تم الربط في',
    'facebook.validUntil': 'الوصول صالح حتى',
    'facebook.days': 'يوم',
    'facebook.permanentAccess': 'وصول دائم (لا ينتهي أبدًا)',
    'facebook.autoRenewHint': 'يُجدَّد تلقائيًا قبل انتهائه عندما يفتح مسؤول لوحة الإدارة وحساب فيسبوك هذا متصل في المتصفح.',
    'facebook.expired': 'انتهت صلاحية الوصول: أعد ربط فيسبوك.',
    'facebook.connected': 'تم ربط فيسبوك',
    'facebook.disconnected': 'تم فصل فيسبوك',
    'facebook.cancelled': 'تم إلغاء ربط فيسبوك',
    'facebook.sdkError': 'تعذر تحميل فيسبوك. تحقق من اتصالك بالإنترنت.',
    'facebook.connectError': 'تعذر ربط فيسبوك',
    'facebook.daysRecovered': 'يوم(أيام) تم استرجاعها في العمال',
    'facebook.reconnectTitle': 'انتهت صلاحية ربط فيسبوك',
    'facebook.reconnectRefusedTitle': 'فيسبوك يرفض الاتصال',
    'facebook.reconnectWorkersNote': 'أعد ربط فيسبوك حتى يستقبل العمال والإحصائيات البيانات من جديد. الأيام التي لم يتمكن العمال من جلبها ستُعاد مراجعتها تلقائيًا.',
    'facebook.reconnectNow': 'إعادة ربط فيسبوك',
    'facebook.later': 'لاحقًا',
    'facebook.expiredOn': 'انتهى وصول فيسبوك في',
    'facebook.seeStats': 'عرض الإحصائيات',
    'facebook.loadError': 'تعذر تحميل حملات فيسبوك',
    'facebook.notConnectedStats': 'اربط حساب فيسبوك من الإعدادات لعرض حملاتك ومصاريفها.',
    'facebook.goToSettings': 'الذهاب إلى الإعدادات',
    'facebook.thisMonth': 'هذا الشهر',
    'facebook.last30': 'آخر 30 يومًا',
    'facebook.lifetime': 'منذ البداية',
    'facebook.search': 'ابحث عن حملة أو حساب…',
    'facebook.allAccounts': 'كل الحسابات',
    'facebook.searchAccount': 'ابحث عن حساب…',
    'facebook.noAccountFound': 'لم يتم العثور على أي حساب',
    'facebook.show': 'عرض الإحصائيات',
    'facebook.showHint': 'اختر الحساب الإعلاني والفترة ثم انقر على « عرض الإحصائيات »: تُقرأ الأرقام من فيسبوك عندها.',
    'facebook.filtersChanged': 'تغيّرت الفلاتر: انقر على « عرض الإحصائيات » للتحديث.',
    'facebook.loadingAccounts': 'جارٍ تحميل الحسابات…',
    'facebook.accountInactive': 'غير نشط',
    'facebook.previous': 'السابق',
    'facebook.next': 'التالي',
    'facebook.of': 'من',
    'facebook.refresh': 'تحديث',
    'facebook.refreshHint': 'إعادة قراءة الأرقام من فيسبوك (وإلا أرقام آخر 5 دقائق)',
    'facebook.warnings': 'تعذرت قراءة بعض الحسابات الإعلانية:',
    'facebook.totalSpend': 'إجمالي المصاريف',
    'facebook.campaigns': 'الحملات',
    'facebook.activeCampaigns': 'نشطة',
    'facebook.impressions': 'مرات الظهور',
    'facebook.clicks': 'النقرات',
    'facebook.spendByCampaign': 'المصاريف حسب الحملة',
    'facebook.spendByCampaignInfo': 'أكثر 10 حملات إنفاقًا خلال الفترة.',
    'facebook.allCampaigns': 'كل الحملات',
    'facebook.campaign': 'الحملة',
    'facebook.page': 'الصفحة',
    'facebook.adAccount': 'الحساب الإعلاني',
    'facebook.status': 'الحالة',
    'facebook.spend': 'المصروف',
    'facebook.noCampaigns': 'لم يتم العثور على أي حملة.',
    'facebook.statusActive': 'نشطة',
    'facebook.statusPaused': 'متوقفة مؤقتًا',
    'facebook.statusArchived': 'مؤرشفة',
    'facebook.statusDeleted': 'محذوفة',
    'facebook.missingRate': 'سعر الدولار غير مُعدّ: لا يمكن تحويل المصاريف بالدولار إلى الدينار.',
    'admin.nav.facebook': 'فيسبوك',
    'admin.nav.facebookWorkers': 'Facebook Ads Workers',
    'admin.title.facebookWorkers': 'Facebook Ads Workers',
    'dollarRate.title': 'سعر الدولار',
    'dollarRate.subtitle': 'يحوّل مصاريف إعلانات فيسبوك (بالدولار) إلى الدينار: إحصائيات فيسبوك و Facebook Ads Workers.',
    'dollarRate.label': 'سعر الصرف',
    'dollarRate.save': 'حفظ',
    'dollarRate.saved': 'تم حفظ سعر الدولار',
    'dollarRate.saveError': 'تعذر حفظ السعر',
    'dollarRate.invalid': 'يجب أن يكون السعر رقمًا أكبر من 0',
    'dollarRate.example': 'مثال:',
    'dollarRate.notSet': 'لم يتم إعداد أي سعر: تبقى المصاريف معروضة بالدولار.',
    'workers.intro': 'كل ليلة على الساعة 2:00، ينشئ كل worker مصروف «Dollar» على منتجه.',
    'workers.introDetail': 'المبلغ = مصروف الحملة في اليوم السابق × سعر الدولار (الإعدادات).',
    'workers.add': 'worker جديد',
    'workers.edit': 'تعديل الـ worker',
    'workers.notConnected': 'لا يوجد حساب فيسبوك مرتبط بهذا المتجر: اربط فيسبوك من الإعدادات.',
    'workers.missingRate': 'سعر الدولار غير مُعدّ: لا يمكن للـ workers تحويل المصاريف إلى الدينار.',
    'workers.loadError': 'تعذر تحميل الـ workers',
    'workers.activeWorkers': 'Workers نشطة',
    'workers.total': 'في المجموع',
    'workers.totalCreated': 'المصاريف المنشأة',
    'workers.nextRun': 'التنفيذ القادم',
    'workers.nextRunHint': 'مصروف اليوم السابق',
    'workers.empty': 'لا يوجد أي worker. أنشئ واحدًا لتحويل مصروف حملة كل يوم إلى مصروف على منتج.',
    'workers.active': 'نشط',
    'workers.paused': 'متوقف',
    'workers.pause': 'إيقاف مؤقت',
    'workers.resume': 'إعادة التفعيل',
    'workers.lastRun': 'آخر تنفيذ',
    'workers.neverRun': 'لم يُنفّذ بعد',
    'workers.run': 'تنفيذ',
    'workers.history': 'السجل',
    'workers.chooseCampaign': 'اختر حملة',
    'workers.chooseProduct': 'اختر منتجًا',
    'workers.created': 'تم إنشاء الـ worker',
    'workers.updated': 'تم تعديل الـ worker',
    'workers.saveError': 'تعذر حفظ الـ worker',
    'workers.deleted': 'تم حذف الـ worker (المصاريف المنشأة تبقى محفوظة)',
    'workers.expenseCreated': 'تم إنشاء المصروف',
    'workers.runError': 'تعذر تنفيذ الـ worker',
    'workers.noSpend': 'لا يوجد مصروف في ذلك اليوم',
    'workers.campaignsError': 'تعذر تحميل حملات فيسبوك',
    'workers.searchCampaign': 'ابحث عن حملة…',
    'workers.loadingCampaigns': 'جارٍ تحميل الحملات…',
    'workers.campaign': 'حملة فيسبوك',
    'workers.product': 'المنتج',
    'workers.productHint': 'يُنشأ مصروف «Dollar» لكل يوم على هذا المنتج.',
    'workers.activeHint': 'عند التوقف لا ينشئ الـ worker أي مصروف؛ وعند إعادة التفعيل يسترجع الأيام الفائتة من فترته.',
    'workers.formHelp': 'كل ليلة على الساعة 2:00، يُضرب مصروف هذه الحملة في اليوم السابق (بالدولار) في سعر الدولار ويُسجَّل كمصروف «Dollar» بتاريخ اليوم السابق.',
    'workers.runTitle': 'تنفيذ الآن',
    'workers.recheck': 'إعادة التحقق',
    'workers.recheckTitle': 'إعادة التحقق من كل المصاريف',
    'workers.recheckHelp': 'يعيد قراءة إنفاق كل يوم من الفترة من فيسبوك: تُضاف الأيام الناقصة أو التي فيها خطأ، ويُصحَّح المبلغ الذي تغيّر في فيسبوك، ويُحذف مصروف اليوم الذي لم يعد فيه إنفاق. اليوم المحتسب مسبقًا يحتفظ بسعر الدولار الذي احتُسب به.',
    'workers.recheckError': 'تعذرت إعادة التحقق من هذا العامل',
    'workers.alertTitleOne': 'عامل فيسبوك في خطأ',
    'workers.alertTitleMany': 'عمال فيسبوك في خطأ',
    'workers.alertIntro': 'هذه الأيام لا تُضاف إلى المصاريف ما دام الخطأ قائمًا. يعيد العامل المحاولة كل ساعة.',
    'workers.alertDays': 'يوم(أيام) غير محسوبة منذ',
    'workers.alertSee': 'عرض العمال',
    'workers.daysChecked': 'أيام تم التحقق منها',
    'workers.upToDate': 'كل شيء محدّث',
    'workers.chargesAdded': 'مصاريف مضافة',
    'workers.chargesFixed': 'مصحّحة',
    'workers.chargesRemoved': 'محذوفة',
    'workers.difference': 'الفرق',
    'workers.yesterday': 'أمس',
    'workers.loadingMore': 'جارٍ التحميل…',
    'workers.scrollForMore': 'مرّر لعرض المزيد',
    'workers.campaignsLabel': 'حملات',
    'workers.refreshCampaigns': 'تحديث',
    'workers.runHelp': 'ينشئ مصروف يوم سابق (مثلًا يوم فائت). اليوم المحتسب مسبقًا لا يُنشأ مرتين أبدًا.',
    'workers.day': 'اليوم',
    'workers.close': 'إغلاق',
    'workers.noHistory': 'لا يوجد أي تنفيذ حاليًا.',
    'workers.status': 'الحالة',
    'workers.amount': 'المبلغ',
    'workers.runAt': 'نُفّذ في',
    'workers.statusSuccess': 'تم إنشاء المصروف',
    'workers.statusNoSpend': 'لا يوجد مصروف',
    'workers.statusError': 'خطأ',
    'workers.startDate': 'تاريخ البداية',
    'workers.endDate': 'تاريخ النهاية',
    'workers.endDateHint': 'فارغ = بدون نهاية',
    'workers.chooseStart': 'اختر تاريخ البداية',
    'workers.endBeforeStart': 'يجب أن يكون تاريخ النهاية بعد تاريخ البداية',
    'workers.pastStartHint': 'تاريخ بداية سابق: تُنشأ مصاريف كل الأيام منذ ذلك التاريخ فور الحفظ.',
    'workers.daysCaughtUp': 'يوم تم استرجاعه',
    'workers.period': 'الفترة',
    'workers.noEnd': 'بدون نهاية',
    'workers.daysDone': 'يوم معالج',
    'workers.finished': 'انتهى',
    'facebook.reach': 'الوصول',
    'facebook.frequency': 'التكرار',
    'facebook.ctr': 'CTR',
    'facebook.cpc': 'CPC',
    'facebook.cpcHint': 'تكلفة النقرة',
    'facebook.cpm': 'CPM',
    'facebook.cpmHint': 'تكلفة 1000 ظهور',
    'facebook.purchases': 'المشتريات',
    'facebook.purchaseShort': 'شراء',
    'facebook.costPerPurchase': 'تكلفة/شراء',
    'facebook.roas': 'ROAS',
    'facebook.messages': 'الرسائل',
    'facebook.costPerMessage': 'تكلفة/رسالة',
    'facebook.leads': 'العملاء المحتملون',

    // Orders filters
    'orders.filters.refine': 'تضييق البحث',
    'orders.filters.activeLabel': 'مرشح نشط',
    'orders.filters.clearTitle': 'مسح عوامل التصفية',
    'orders.filters.hide': 'إخفاء',
    'orders.filters.search': 'بحث',
    'orders.filters.searchPlaceholder': 'المعرف، الاسم، الهاتف...',
    'orders.filters.platform': 'المنصة',
    'orders.filters.allFem': 'الكل',
    'orders.filters.sync': 'المزامنة',
    'orders.filters.synced': 'متزامن',
    'orders.filters.notSynced': 'غير متزامن',
    'orders.filters.attemptReserved': 'محاولة الاتصال',
    'orders.filters.reserved': 'مع محاولة',
    'orders.filters.notReserved': 'بدون محاولة',
    'orders.filters.exchange': 'استبدال',
    'orders.filters.notExchange': 'بدون استبدال',
    'orders.filters.printed': 'الطباعة',
    'orders.filters.notPrinted': 'غير مطبوعة',
    'orders.filters.printedOnly': 'مطبوعة',
    'orders.printed': 'مطبوعة',
    'orders.printedTitle': 'تمت طباعة ملصق هذا الطلب مسبقًا',
    'orders.filters.packed': 'التغليف',
    'orders.filters.notPacked': 'غير مغلفة',
    'orders.filters.packedOnly': 'مغلفة',
    'orders.packed': 'مغلفة',
    'orders.packedTitle': 'تم تغليف طرد هذا الطلب (تم مسح الباركود)',
    'orders.selection.count': 'طلب(ات) محددة',
    'orders.selection.printedCount': 'مطبوعة مسبقًا',
    'orders.selection.excludePrinted': 'استبعاد المطبوعة',
    'orders.selection.excludedPrinted': 'طلب(ات) مطبوعة أُزيلت من التحديد',
    'orders.selection.clear': 'إلغاء تحديد الكل',
    'orders.filters.products': 'المنتجات',
    'orders.filters.allProducts': 'كل المنتجات',
    'orders.filters.searchProduct': 'البحث عن منتج...',
    'orders.filters.noProduct': 'لم يتم العثور على منتج',
    'orders.filters.deselectAll': 'إلغاء تحديد الكل',

    // Orders header / toolbar
    'orders.header.title': 'الطلبات',
    'orders.header.newOrder': 'طلب جديد',
    'orders.header.printAll': 'طباعة الكل',
    'orders.header.printSelected': 'طباعة المحدد',
    'orders.header.printStock': 'طباعة المخزون',
    'orders.header.pay': 'دفع',
    'orders.header.syncAll': 'مزامنة الكل',
    'orders.header.syncSelected': 'مزامنة المحدد',
    'orders.header.syncingTitle': 'جارٍ مزامنة الطلب',
    'orders.header.syncingWait': 'يرجى الانتظار، لا تغلق هذه النافذة.',
    'orders.header.order': 'طلب',
    'orders.header.exportNote': 'ملاحظة: سيشمل التصدير الطلبات المصفّاة حاليًا فقط',
    'orders.header.ordersWord': 'طلبات',
    'orders.modal.sync': 'مزامنة',
    'orders.modal.print': 'طباعة',
    'orders.modal.track': 'تتبع',
    'orders.modal.import': 'استيراد',
    'orders.modal.syncTitle': 'تأكيد المزامنة',
    'orders.modal.printTitle': 'تأكيد الطباعة',
    'orders.modal.trackTitle': 'تأكيد التتبع',
    'orders.modal.importTitle': 'استيراد الطلبات',
    'orders.modal.allOrdersWord': 'كل الطلبات',
    'orders.modal.allLabelsWord': 'كل الملصقات',
    'orders.modal.ordersSelectedQ': 'طلب محدد؟',
    'orders.modal.labelsSelectedQ': 'ملصق محدد؟',
    'orders.modal.syncDesc': 'ستؤدي هذه العملية إلى مزامنة الطلبات مع النظام الخارجي. هل تريد المتابعة؟',
    'orders.modal.printDesc': 'ستنشئ هذه العملية مستند طباعة بملصق واحد لكل صفحة. هل تريد المتابعة؟',
    'orders.modal.trackDesc': 'ستتيح هذه العملية تتبع الطلبات المحددة. هل تريد المتابعة؟',
    'orders.modal.syncWarnSelected': 'ستتم مزامنة الطلبات المحددة فقط.',
    'orders.modal.syncWarnAll': 'ستتم مزامنة جميع الطلبات المصفّاة حاليًا.',
    'orders.modal.printWarnSelected': 'ستتم طباعة الطلبات المحددة فقط.',
    'orders.modal.printWarnAll': 'ستتم طباعة جميع الطلبات المصفّاة حاليًا.',
    'orders.modal.trackWarnSelected': 'سيتم تتبع الطلبات المحددة فقط.',
    'orders.modal.trackWarnAll': 'سيتم تتبع جميع الطلبات المصفّاة حاليًا.',
    'orders.modal.importSelectFile': 'اختر ملف Excel',
    'orders.modal.importPreview': 'معاينة الباركود',
    'orders.modal.importPreviewFirst': 'الأولى',
    'orders.modal.importHint': 'يجب أن يحتوي ملف Excel على عمود باسم "barCode" يحتوي على باركود الطلبات المراد استيرادها.',
    'orders.delete.title': 'حذف الطلب',
    'orders.delete.q1': 'هل أنت متأكد أنك تريد حذف الطلب',
    'orders.delete.q2': '؟ لا يمكن التراجع عن هذا الإجراء.',
    'status.allOrders': 'كل الطلبات',
    'status.abandoned': 'سلة متروكة',
    'status.pending': 'قيد الانتظار',
    'status.confirmed': 'مؤكدة',
    'status.readyToShip': 'جاهز للشحن',
    'status.shipped': 'مشحونة',
    'status.delivered': 'تم التوصيل',
    'status.canceled': 'ملغاة',
    'status.return': 'إرجاع',
    'status.returnReceived': 'تم استلام الإرجاع',
    'status.dechet': 'تالف',
    'status.paid': 'مدفوع',
    'orders.table.selection': 'تحديد',
    'orders.table.idRef': 'المعرّف / المرجع',
    'orders.table.client': 'العميل',
    'orders.table.totalTTC': 'الإجمالي شامل الضريبة',
    'orders.table.comment': 'تعليق',
    'orders.table.clientReview': 'رأي العميل',
    'orders.table.syncro': 'مزامنة',
    'orders.table.callReserved': 'محاولة الاتصال',
    'orders.table.statusBtn': 'الحالة',
    'orders.table.changeStatusTitle': 'تغيير حالة الطلبات المحددة',
    'orders.table.selectOrdersTitle': 'يرجى تحديد طلبات',
    'orders.table.changeStatus': 'تغيير الحالة',
    'orders.table.selectedFem': 'محددة',

    // Common admin actions / labels
    'common.search': 'بحث',
    'common.retry': 'إعادة المحاولة',
    'common.add': 'إضافة',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.close': 'إغلاق',
    'common.confirm': 'تأكيد',
    'common.apply': 'تطبيق',
    'common.reset': 'إعادة تعيين',
    'common.filter': 'تصفية',
    'common.filters': 'عوامل التصفية',
    'common.clear': 'مسح',
    'common.export': 'تصدير',
    'common.import': 'استيراد',
    'common.print': 'طباعة',
    'common.actions': 'إجراءات',
    'common.status': 'الحالة',
    'common.date': 'التاريخ',
    'common.total': 'المجموع',
    'common.name': 'الاسم',
    'common.price': 'السعر',
    'common.quantity': 'الكمية',
    'common.loading': 'جاري التحميل...',
    'common.noData': 'لا توجد بيانات',
    'common.all': 'الكل',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.previous': 'السابق',
    'common.details': 'التفاصيل',
  },
};

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  dir: 'ltr',
  setLanguage: () => {},
  t: (key) => key,
});

const getInitialLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && LANGUAGES[stored]) {
      return stored;
    }
  } catch (e) {
    // localStorage may be unavailable (private mode / SSR) — fall back silently
  }
  return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const applyDocumentLanguage = useCallback((lang) => {
    const config = LANGUAGES[lang] || LANGUAGES[DEFAULT_LANGUAGE];
    if (typeof document !== 'undefined') {
      document.documentElement.lang = config.code;
      document.documentElement.dir = config.dir;
    }
  }, []);

  // Apply direction/lang on mount and whenever the language changes
  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language, applyDocumentLanguage]);

  const setLanguage = useCallback((lang) => {
    if (!LANGUAGES[lang]) return;
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch (e) {
      // Ignore persistence errors
    }
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key) => {
      const dict = TRANSLATIONS[language] || TRANSLATIONS[DEFAULT_LANGUAGE];
      return dict[key] ?? TRANSLATIONS[DEFAULT_LANGUAGE][key] ?? key;
    },
    [language]
  );

  const value = {
    language,
    dir: (LANGUAGES[language] || LANGUAGES[DEFAULT_LANGUAGE]).dir,
    languages: LANGUAGES,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
